import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../PurchaseOrderDetailsPage/PurchaseOrderDetailsPage.css";
import {
  getFoodingOrderById,
  updateFoodingOrderItemPrice,
  updateFoodingOrderStatus,
} from "../../services/foodingOrderService";
import { getProductById } from "../../services/productService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import UpdatePriceModal from "../../compoonents/UpdatePriceModel";

const SHIPMENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "in_transit", label: "In Transit" },
  { value: "shipped", label: "Shipped" },
  { value: "packed", label: "Packed" },
  { value: "checked_in", label: "Checked In" },
  { value: "checked_out", label: "Checked Out" },
  { value: "not_checked", label: "Not Checked" },
  { value: "arrived_at_local_hub", label: "Arrived At Local Hub" },
  { value: "arrived_at_nigeria_hub", label: "Arrived At Nigeria Hub" },
  { value: "arrived_at_uk_hub", label: "Arrived At UK Hub" },
  { value: "completed", label: "Completed" },
  { value: "canceled", label: "Canceled" },
];

const formatShipmentStatus = (status: string): string => {
  const match = SHIPMENT_STATUS_OPTIONS.find(
    (o) => o.value.toLowerCase() === (status || "").toLowerCase()
  );
  return match ? match.label : (status || "N/A").replace(/_/g, " ");
};

const getStatusColor = (status: string): string => {
  switch ((status || "").toLowerCase()) {
    case "pending":
    case "not_checked":
      return "bg-yellow-100 text-yellow-800";
    case "shipped":
    case "in_transit":
      return "bg-blue-100 text-blue-800";
    case "packed":
    case "checked_in":
    case "checked_out":
      return "bg-indigo-100 text-indigo-800";
    case "completed":
      return "bg-green-100 text-green-800";
    case "canceled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`status-badge ${getStatusColor(status)}`}>
    {formatShipmentStatus(status)}
  </span>
);

const currencySymbol = (currency?: string) => {
  switch (currency) {
    case "GBP":
      return "£";
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "NGN":
      return "₦";
    default:
      return currency ? `${currency} ` : "";
  }
};

const FoodingOrderDetailsPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productNames, setProductNames] = useState<Record<string, string>>({});

  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [showUpdatePriceModal, setShowUpdatePriceModal] = useState(false);
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const loadOrder = async (id: string) => {
    const data = await getFoodingOrderById(id);
    setOrder(data);
    return data;
  };

  useEffect(() => {
    if (!orderId) {
      setError("No Order ID provided.");
      setLoading(false);
      return;
    }

    const fetchOrderDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await loadOrder(orderId);

        const items = data?.items || [];
        const names: Record<string, string> = {};
        await Promise.all(
          items.map(async (item: any) => {
            if (!item.productId) return;
            try {
              const res = await getProductById(item.productId);
              names[item.productId] = res.product?.name || item.productId;
            } catch {
              names[item.productId] = item.productId;
            }
          })
        );
        setProductNames(names);
      } catch {
        setError("Failed to fetch order details.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  const handleUpdatePrice = async (newPrice: number) => {
    if (!selectedItem || !orderId) return;

    setIsUpdatingPrice(true);
    try {
      await updateFoodingOrderItemPrice(orderId, selectedItem.id, newPrice);
      await loadOrder(orderId);
      toast.success("Price updated successfully!");
      setShowUpdatePriceModal(false);
      setSelectedItem(null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to update price. Please try again."
      );
    } finally {
      setIsUpdatingPrice(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!order?.id) return;
    setIsUpdatingStatus(true);
    try {
      await updateFoodingOrderStatus(order.id, newStatus);
      setOrder((prev: any) => ({ ...prev, shipmentStatus: newStatus }));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="po-details-not-found">
        <h2>Loading Order...</h2>
      </div>
    );
  }
  if (error) {
    return (
      <div className="po-details-not-found">
        <h2>{error}</h2>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="po-details-not-found">
        <h2>Order Not Found</h2>
      </div>
    );
  }

  const customerName =
    `${order.user?.firstName || ""} ${order.user?.lastName || ""}`.trim() ||
    "N/A";
  const shipmentStatus = order.shipmentStatus || order.status || "pending";

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="po-details-page">
        <div className="po-details-header">
          <div className="customer-info">
            <h2 className="ttiimm">
              <span
                onClick={() => navigate("/purchase-orders")}
                className="back-link"
                style={{ cursor: "pointer" }}
              >
                Fooding
              </span>{" "}
              / Order Details
            </h2>
            <h1>
              {customerName}
              <StatusBadge status={shipmentStatus} />
            </h1>
            <p style={{ margin: "8px 0 0", color: "#666", fontSize: "14px" }}>
              {order.user?.email}
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end" }}>
            <label style={{ fontSize: "12px", color: "#666" }}>Shipment Status</label>
            <select
              value={shipmentStatus}
              disabled={isUpdatingStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                minWidth: "200px",
              }}
            >
              {SHIPMENT_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "24px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <span style={{ fontSize: "12px", color: "#888" }}>Total Amount</span>
            <p style={{ margin: "4px 0 0", fontWeight: 600 }}>
              {currencySymbol(order.currency)}
              {Number(order.totalAmount || 0).toLocaleString()}
            </p>
          </div>
          <div>
            <span style={{ fontSize: "12px", color: "#888" }}>Payment Status</span>
            <p style={{ margin: "4px 0 0", fontWeight: 600 }}>
              {order.paymentStatus || "—"}
            </p>
          </div>
          <div>
            <span style={{ fontSize: "12px", color: "#888" }}>Payment Method</span>
            <p style={{ margin: "4px 0 0", fontWeight: 600 }}>
              {order.paymentMethod || order.transaction?.paymentMethod || "—"}
            </p>
          </div>
          <div>
            <span style={{ fontSize: "12px", color: "#888" }}>Date</span>
            <p style={{ margin: "4px 0 0", fontWeight: 600 }}>
              {order.createdAt
                ? new Date(order.createdAt).toLocaleString()
                : "—"}
            </p>
          </div>
        </div>

        <div className="cart-con">Order Items</div>

        <div className="cart-items-list">
          {(order.items || []).map((item: any, index: number) => {
            const productName =
              productNames[item.productId] || item.productId || "Product";
            const price = Number(item.pricePerItem || 0);
            const qty = item.quantity || 1;

            return (
              <div
                key={item.id}
                className="cart-item-card"
                style={{
                  backgroundColor: index % 2 === 0 ? "#FFF5EE" : "#f9f9f9",
                }}
              >
                <div className="cart-item1">
                  <div className="item-content1">
                    <div className="content-section">
                      <div className="items-section">
                        <span className="section-label">Product</span>
                        <p className="items-text">{productName}</p>
                      </div>
                      <div className="cost-section">
                        <span className="section-label">Price × Qty</span>
                        <div className="cost-row">
                          <span className="cost-amount">
                            {currencySymbol(order.currency)}
                            {price.toLocaleString()} × {qty} ={" "}
                            {currencySymbol(order.currency)}
                            {(price * qty).toLocaleString()}
                          </span>
                          <button
                            type="button"
                            className="update-btn1"
                            onClick={() => {
                              setSelectedItem(item);
                              setShowUpdatePriceModal(true);
                            }}
                          >
                            Update Price
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {order.transaction && (
          <div
            className="payment-summary-card"
            style={{
              marginTop: "24px",
              padding: "16px",
              background: "#f8f9fa",
              borderRadius: "8px",
            }}
          >
            <h4 style={{ margin: "0 0 12px" }}>Transaction</h4>
            <p style={{ margin: "4px 0", fontSize: "14px" }}>
              Reference: {order.transaction.transactionReference || "—"}
            </p>
            <p style={{ margin: "4px 0", fontSize: "14px" }}>
              Amount: {currencySymbol(order.transaction.currency || order.currency)}
              {Number(order.transaction.amount || 0).toLocaleString()}
            </p>
          </div>
        )}
      </div>

      {showUpdatePriceModal && selectedItem && (
        <UpdatePriceModal
          item={{
            item: productNames[selectedItem.productId] || "Product",
            amount: Number(selectedItem.pricePerItem || 0),
            currency: order.currency || "GBP",
          }}
          isSubmitting={isUpdatingPrice}
          onClose={() => {
            if (isUpdatingPrice) return;
            setShowUpdatePriceModal(false);
            setSelectedItem(null);
          }}
          onSave={handleUpdatePrice}
        />
      )}
    </>
  );
};

export default FoodingOrderDetailsPage;
